import isString from 'lodash/isString';
import memoize from 'lodash/memoize';

import * as Constants from '../constants';
import { type FilterNavTypes } from '../constants/FilterTypeNav';
import Logger from '../tools/Logger';
import { type ValueOf } from '../types';

import { isAPSPandoraStationEnabled } from './ListenerUtils';

const log = Logger.child({ component: 'SourceTypeUtil' });

export type SourceTypeValue = ValueOf<typeof Constants.SourceTypes.SourceTypes>;
export type PandoraTypeValue = ValueOf<typeof Constants.SourceTypes.PandoraTypes>;
export type StationTypeValue = typeof Constants.SourceTypes.StationTypes[number];

export function getPandoraTypeFromPandoraId(pandoraId: string): string | null {
  if (!pandoraId) {
    return null;
  }
  const maybePandoraType = pandoraId && (pandoraId.split(':')[0] as PandoraTypeValue);
  const isPandoraType =
    maybePandoraType !== ''
      ? Object.values(Constants.SourceTypes.PandoraTypes).includes(maybePandoraType)
      : false;
  if (!isPandoraType) {
    log.info(`getPandoraTypeFromPandoraId failed to find type for ${pandoraId}`);
  }
  return isPandoraType ? maybePandoraType : null;
}

export function getSourceTypeFromPandoraId(pandoraId: string): SourceTypeValue | null {
  const pandoraType = getPandoraTypeFromPandoraId(pandoraId);
  const source = pandoraType && Constants.SourceTypes.PandoraTypeSources[pandoraType];
  const sourceType =
    source &&
    Constants.SourceTypes.SourceTypes[source as keyof typeof Constants.SourceTypes.SourceTypes];
  if (!sourceType) {
    log.info(`getSourceTypeFromPandoraId failed to find type for ${pandoraId}`);
    return null;
  }
  return sourceType;
}

export function getDesktopBridgeSourceType(sourceType: SourceTypeValue): 'station' | 'ondemand' {
  const stationSourceTypes: typeof Constants.SourceTypes.SourceTypes[keyof typeof Constants.SourceTypes.SourceTypes][] =
    [Constants.SourceTypes.SourceTypes.AUTOPLAY, Constants.SourceTypes.SourceTypes.STATION];
  return !sourceType || stationSourceTypes.includes(sourceType) ? 'station' : 'ondemand';
}

export function isPseudoSource(
  sourceType: typeof Constants.SourceTypes.PseudoSourceTypes[number],
): boolean {
  return Constants.SourceTypes.PseudoSourceTypes.indexOf(sourceType) !== -1;
}

export function getPseudoSourceTypeFromUrlParams(
  parentType: string,
  pseudoType: string,
): SourceTypeValue | null {
  if (pseudoType === Constants.Catalog.SourceTypeUrlParams.TOP_SONGS) {
    return Constants.SourceTypes.SourceTypes.ARTIST_TOP_SONGS;
  } else if (pseudoType === Constants.Catalog.SourceTypeUrlParams.ALL_SONGS) {
    if (parentType === Constants.Catalog.SourceTypeUrlParams.ARTIST) {
      return Constants.SourceTypes.SourceTypes.ARTIST_ALL_SONGS;
    }
    return Constants.SourceTypes.SourceTypes.COLLECTED_TRACKS;
  } else if (pseudoType === Constants.Catalog.SourceTypeUrlParams.THUMBED_UP) {
    return Constants.SourceTypes.SourceTypes.STATION_THUMB_UP;
  }
  return null;
}

export function getCollectedTrackPseudoId(listenerId: string): string {
  return `${Constants.SourceTypes.PandoraTypes.COLLECTED_TRACKS}:${listenerId}`;
}

export function getStationFeedbackPseudoId(stationId: string): string {
  return `${Constants.SourceTypes.PandoraTypes.STATION_THUMB_UP}:${stationId}`;
}

export function getCollectedAlbumTracksId(albumId: string): string {
  return `${Constants.SourceTypes.PandoraTypes.COLLECTED_ALBUM_TRACKS}:${
    Constants.SourceTypes.PseudoObfuscations[Constants.SourceTypes.PandoraTypes.ALBUM]
  }:${albumId.split(':')[1]}`;
}

export function getArtistStationFactoryId(artistId: string): string {
  return `${Constants.SourceTypes.PandoraTypes.STATION}:${
    Constants.SourceTypes.PseudoObfuscations[Constants.SourceTypes.PandoraTypes.ARTIST]
  }:${artistId.slice(1)}`;
}

export function getArtistAllSongsId(artistId: string): string {
  const artistPrefix = getPandoraTypeFromPandoraId(artistId);
  return `${Constants.SourceTypes.PandoraTypes.ARTIST_ALL_SONGS}:${
    // @ts-expect-error TS(2538) FIXME: Type 'null' cannot be used as an index type.
    Constants.SourceTypes.PseudoObfuscations[artistPrefix]
  }:${artistId.split(':')[1]}`;
}

export function getArtistTopSongsId(artistId: string): string {
  const artistPrefix = getPandoraTypeFromPandoraId(artistId);
  return `${Constants.SourceTypes.PandoraTypes.ARTIST_TOP_SONGS}:${
    // @ts-expect-error TS(2538) FIXME: Type 'null' cannot be used as an index type.
    Constants.SourceTypes.PseudoObfuscations[artistPrefix]
  }:${artistId.split(':')[1]}`;
}

export function isStationType(type: PandoraTypeValue): boolean {
  const stationPandoraTypes: PandoraTypeValue[] = [
    Constants.SourceTypes.PandoraTypes.STATION,
    Constants.SourceTypes.PandoraTypes.GENRE,
    Constants.SourceTypes.PandoraTypes.HYBRID_STATION,
  ];

  const possibleStationTypes: (PandoraTypeValue | StationTypeValue)[] = [
    ...Constants.SourceTypes.StationTypes,
    ...stationPandoraTypes,
  ];

  return possibleStationTypes.includes(type);
}

export function isPandoraTypeAPSEnabled(state: $TSFixMe, pandoraType: PandoraTypeValue): boolean {
  const isAPSStationEnabled = isAPSPandoraStationEnabled(state);
  return !isStationType(pandoraType) || isAPSStationEnabled;
}

export function isPlaylistPandoraId(pandoraId: string): boolean {
  if (isString(pandoraId)) {
    return pandoraId.startsWith(Constants.SourceTypes.PandoraTypes.PLAYLIST);
  }
  return false;
}

export function isPodcastEpisodePandoraId(pandoraId: string): boolean {
  if (isString(pandoraId)) {
    return pandoraId.startsWith(Constants.SourceTypes.PandoraTypes.EPISODE);
  }
  return false;
}

export function isPodcastPandoraType(type: PandoraTypeValue): boolean {
  return (
    type === Constants.SourceTypes.PandoraTypes.PODCAST ||
    type === Constants.SourceTypes.PandoraTypes.EPISODE
  );
}

function getFilterTypes(
  shouldShowAllFilterTypes: $TSFixMe,
  isSearchFilter: $TSFixMe,
): Array<ValueOf<typeof FilterNavTypes>> {
  let filterTypes: Array<ValueOf<typeof FilterNavTypes>> = [];
  if (shouldShowAllFilterTypes) {
    filterTypes = [...Constants.FilterNav.AllFilterTypes];
  } else {
    filterTypes = [...Constants.FilterNav.DefaultFilterTypes];
  }
  if (!isSearchFilter) {
    filterTypes.push(Constants.FilterNav.FilterNavTypes.EPISODES);
  }
  return filterTypes;
}

export const getMemoizedFilterTypes = memoize(getFilterTypes, (...args) => JSON.stringify(args));
